import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { saveEmailBD, getCategories, getSections, likeProduct } from '../../util/api';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getParamsListing, logEvent, logPixelFacebook } from '../../util/logsEvent';

const { Money } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/LandingPage/SET_INITIAL_STATE';
export const QUERY_LISTINGS_REQUEST = 'app/LandingPage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/LandingPage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/LandingPage/QUERY_LISTINGS_ERROR';

export const SEND_EMAIL_REQUEST = 'app/LandingPage/SEND_EMAIL_REQUEST';
export const SEND_EMAIL_SUCCESS = 'app/LandingPage/SEND_EMAIL_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const FETCH_HOME_SECTIONS_REQUEST = 'app/LandingPage/FETCH_HOME_SECTIONS_REQUEST';
export const FETCH_HOME_SECTIONS_SUCCESS = 'app/LandingPage/FETCH_HOME_SECTIONS_SUCCESS';
export const UPDATE_HOME_SECTION_ITEMS = 'app/LandingPage/UPDATE_HOME_SECTION_ITEMS';
export const FETCH_HOME_SECTIONS_ERROR = 'app/LandingPage/FETCH_HOME_SECTIONS_ERROR';

export const TOGGLE_LIKE_REQUEST = 'app/LandingPage/TOGGLE_LIKE_REQUEST';
export const TOGGLE_LIKE_SUCCESS = 'app/LandingPage/TOGGLE_LIKE_SUCCESS';
export const TOGGLE_LIKE_ERROR = 'app/LandingPage/TOGGLE_LIKE_ERROR';
export const UPDATE_SECTION_ITEM_LIKES = 'app/LandingPage/UPDATE_SECTION_ITEM_LIKES';

// ================ Reducer ================ //

const initialState = {
  featuredListingsIds: [],
  queryListingsError: null,
  queryListingsInProgress: false,
  homePageSections: [],
  homeSectionsError: null,
  homeSectionsInProgress: false,
  likeInProgress: null,
  likeError: null,
};

export const sendEmailRequest = option => ({
  type: SEND_EMAIL_REQUEST,
  payload: option,
});

export const sendEmailSuccess = wasSave => ({
  type: SEND_EMAIL_SUCCESS,
  payload: wasSave,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

const resultIds = data => data.data.map(l => l.id);

// Helper function to update the likes for an item in a specific section
const updateSectionItemLikesInState = (state, listingId, userId, isLiked) => {
  let updatedSections = state.homePageSections.map(section => {
    // Check if this section has items
    if (!section.items || !section.items.length) {
      return section;
    }

    // Find the item in this section
    const itemIndex = section.items.findIndex(item => item.id === listingId);
    
    // If item not found in this section, return section unchanged
    if (itemIndex === -1) {
      return section;
    }

    // Get the current item
    const item = section.items[itemIndex];
    
    // Get current likedBy array or create empty array if it doesn't exist
    const likedBy = item.likedBy || [];
    
    // Update likedBy and likes count based on the action
    let updatedLikedBy;
    if (isLiked && !likedBy.includes(userId)) {
      // User liked the item, add to likedBy
      updatedLikedBy = [...likedBy, userId];
    } else if (!isLiked && likedBy.includes(userId)) {
      // User unliked the item, remove from likedBy
      updatedLikedBy = likedBy.filter(id => id !== userId);
    } else {
      // No change needed, return section unchanged
      return section;
    }
    
    // Update likes count
    const likesCount = updatedLikedBy.length;
    
    // Create updated item
    const updatedItem = {
      ...item,
      likedBy: updatedLikedBy,
      likes: likesCount
    };
    
    // Create new items array with the updated item
    const updatedItems = [...section.items];
    updatedItems[itemIndex] = updatedItem;
    
    // Return updated section
    return {
      ...section,
      items: updatedItems
    };
  });
  
  return {
    ...state,
    homePageSections: updatedSections
  };
};

export default function landingPageReducer(state = initialState, action = {}) {
	const { type, payload } = action;
  switch (type) {
  	case SET_INITIAL_STATE:
      return { ...initialState };
    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        featuredListingsIds: state.featuredListingsIds,
        queryListingsInProgress: true,
        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { 
        ...state,
        featuredListingsIds: resultIds(payload.listings.data),
        queryListingsInProgress: false
      };
    case QUERY_LISTINGS_ERROR:
      return { ...state, featuredListingsIds: [], queryListingsError: payload, queryListingsInProgress: false };
    case FETCH_HOME_SECTIONS_REQUEST:
      return {
        ...state,
        homeSectionsInProgress: true,
        homeSectionsError: null,
      };
    case FETCH_HOME_SECTIONS_SUCCESS:
      return {
        ...state,
        homePageSections: payload,
        homeSectionsInProgress: false,
      };
    case UPDATE_HOME_SECTION_ITEMS:
      return {
        ...state,
        homePageSections: state.homePageSections.map(section => 
          section.name === payload.sectionName 
            ? { ...section, items: payload.items }
            : section
        ),
      };
    case FETCH_HOME_SECTIONS_ERROR:
      return {
        ...state,
        homeSectionsError: payload,
        homeSectionsInProgress: false,
      };
    case TOGGLE_LIKE_REQUEST:
      return {
        ...state,
        likeInProgress: payload.listingId,
        likeError: null,
      };
    case TOGGLE_LIKE_SUCCESS:
      return {
        ...state,
        likeInProgress: null,
      };
    case TOGGLE_LIKE_ERROR:
      return {
        ...state,
        likeInProgress: null,
        likeError: payload,
      };
    case UPDATE_SECTION_ITEM_LIKES:
      return updateSectionItemLikesInState(
        state, 
        payload.listingId, 
        payload.userId, 
        payload.isLiked
      );
  	default:
  		return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const queryListingsRequest = () => ({
  type: QUERY_LISTINGS_REQUEST,
});

export const queryListingsSuccess = listings => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listings },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchHomeSectionsRequest = () => ({
  type: FETCH_HOME_SECTIONS_REQUEST,
});

export const fetchHomeSectionsSuccess = sections => ({
  type: FETCH_HOME_SECTIONS_SUCCESS,
  payload: sections,
});

export const updateHomeSectionItems = (sectionName, items) => ({
  type: UPDATE_HOME_SECTION_ITEMS,
  payload: { sectionName, items },
});

export const fetchHomeSectionsError = error => ({
  type: FETCH_HOME_SECTIONS_ERROR,
  payload: error,
});

export const toggleLikeRequest = listingId => ({
  type: TOGGLE_LIKE_REQUEST,
  payload: { listingId },
});

export const toggleLikeSuccess = () => ({
  type: TOGGLE_LIKE_SUCCESS,
});

export const toggleLikeError = error => ({
  type: TOGGLE_LIKE_ERROR,
  payload: error,
});

export const updateSectionItemLikes = (listingId, userId, isLiked) => ({
  type: UPDATE_SECTION_ITEM_LIKES,
  payload: { listingId, userId, isLiked },
});

// ================ Thunks ================ //

export const queryFeaturedListings = () => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest());

  const params = {
  	'meta_featured': true,
    per_page: 100,
    include: ['author', 'images', 'createdAt', 'author.profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    'fields.listing': ['title', 'price', 'publicData', 'metadata'],
    'limit.images': 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      // Pick only the id and type properties from the response listings
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const fetchHomeSections = () => async dispatch => {
  dispatch(fetchHomeSectionsRequest());
  try {
    const response = await getCategories();
    dispatch(fetchHomeSectionsSuccess(response));

    // For each section that needs items, fetch them
    const fetchPromises = response
      .filter(section => !section.items || section.items.length === 0)
      .map(section => 
        getSections({ name: section.name })
          .then(sectionResponse => {
            dispatch(updateHomeSectionItems(section.name, sectionResponse.items));
          })
          .catch(error => {
            console.error(`Error fetching items for section ${section.name}:`, error);
          })
      );

    await Promise.all(fetchPromises);
    return response;
  } catch (error) {
    dispatch(fetchHomeSectionsError(storableError(error)));
  }
};

export const sendEmail = (email) => (dispatch, getState, sdk) => {
  dispatch(sendEmailRequest(true));
  return saveEmailBD({ email }).then((response) => {
    dispatch(sendEmailSuccess(response.wasSave));
    dispatch(sendEmailRequest(false));
    return response;
  }).catch(e => {
    dispatch(showUserError(storableError(e)))
    dispatch(sendEmailRequest(false));
  });
};

// Landing page specific toggleLike action
export const toggleLike = (userId, listingId) => dispatch => {
  dispatch(toggleLikeRequest(listingId));

  return likeProduct({ user: userId, itemId: listingId })
    .then(response => {
      // Get the updated listing data
      const listing = response.data.data;
      const metadata = listing.attributes.metadata || {};
      const likedBy = metadata.likedBy || [];
      const isLiked = likedBy.includes(userId);

      // Update the section item likes
      dispatch(updateSectionItemLikes(listingId, userId, isLiked));

      // Format price for marketplace entities
      const price = listing.attributes.price;
      if (price) {
        response.data.data.attributes.price = new Money(price.amount, price.currency);
      }

      // Add marketplace entities and dispatch success
      dispatch(addMarketplaceEntities(response));
      dispatch(toggleLikeSuccess());

      // Log events
      logEvent('favorite submitted', getParamsListing({ 
        ...listing, 
        author: response?.data?.included?.[0] 
      }));
      logPixelFacebook('AddToWishlist');

      return response;
    })
    .catch(e => {
      dispatch(toggleLikeError(storableError(e)));
      throw e;
    });
};

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  return dispatch(fetchHomeSections());
};